// Copyright ©️ 2026 eVolve MEP, LLC

import type { GridState } from 'ag-grid-enterprise';

import { doNotSaveColumnIdentifier } from 'helpers/ag-grid/baseColumnDef';
import type { SavedGridState } from 'helpers/ag-grid/TableViewPanel/types';
import type { WithRequiredIsh } from 'helpers/helper-types';
import { isNil } from 'helpers/isNotNil';

const removeLockedColIdsFromArray = (arr: string[]) =>
  arr.filter((colId) => !colId.startsWith(doNotSaveColumnIdentifier));

/** Reduces the grid's full state down to the fields a table view saves, excluding its locked columns. */
export const getSavedGridState = ({
  filter,
  sort,
  version,
  columnOrder,
  columnPinning,
  columnSizing,
  columnVisibility,
}: GridState): WithRequiredIsh<SavedGridState> => ({
  filter,
  sort,
  version,
  columnOrder: isNil(columnOrder)
    ? undefined
    : { ...columnOrder, orderedColIds: removeLockedColIdsFromArray(columnOrder.orderedColIds) },
  columnPinning: isNil(columnPinning)
    ? undefined
    : {
        ...columnPinning,
        leftColIds: removeLockedColIdsFromArray(columnPinning.leftColIds),
        rightColIds: removeLockedColIdsFromArray(columnPinning.rightColIds),
      },
  columnSizing: isNil(columnSizing)
    ? undefined
    : {
        ...columnSizing,
        columnSizingModel: columnSizing.columnSizingModel.filter(
          ({ colId }) => !colId.startsWith(doNotSaveColumnIdentifier),
        ),
      },
  columnVisibility: isNil(columnVisibility)
    ? undefined
    : { ...columnVisibility, hiddenColIds: removeLockedColIdsFromArray(columnVisibility.hiddenColIds) },
});
